import { dayKey } from '@/utils/studioDay';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { startOfDay, format } from 'date-fns';
import { AlertTriangle, Check, ExternalLink, MessageSquare, Phone, Radio, UserX, X } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { resolveConfirmationOutcome, markNoShow } from '@/domain/confirmationFlow';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import { checkInBooking } from '@/domain/sessionFlow';
import { lateMinutes } from '@/domain/lateness';
import { startOutboundCall } from '@/domain/callFlow';
import { confirmationWorkDue, isConfirmationEscalated, type ConfirmationLane } from '@/domain/confirmationWorklist';
import { askArtistToConfirm } from '@/domain/confirmationEscalationFlow';
import { canMarkConfirmed } from '@/domain/permissions';
import { selfPersonIdFromSession } from '@/utils/commsHelpers';
import { buildHeatmap, buildDaySchedule } from '@/domain/queueEngine';
import { canScheduleBookingOn } from '@/domain/artistAdmin';
import { hourToIso } from '@/domain/depositWindow';
import { buildConfirmationThread } from '@/utils/confirmationTemplates';
import { depositChip as buildDepositChip, waiverChip as buildWaiverChip } from '@/components/customer-card/chips';
import { customerName, simTimeFull, simTimeShort } from '@/utils/format';
import {
  availabilityCellState,
  intensityClass,
  DAY_OFF_MARKER,
} from '@/components/queue/availabilityCell';
import ConversationThread from '@/components/ConversationThread';
import CustomerComposer from '@/components/CustomerComposer';
import type { Booking, CustomerRecord, RescheduleReason, SessionLength } from '@/domain/types';
import { currentActorId } from '@/domain/actor';

interface Props {
  callId: string;
  onClose: () => void;
  /**
   * Late-arrival mode (opened from the floor's Contact button). Surfaces how
   * late the client is and adds two floor-only outcomes - check in (they turned
   * up) and mark no-show - plus a lateness-reason note, on top of the standard
   * call / reschedule / cancel controls.
   */
  lateArrival?: boolean;
}

type LocalOutcome = 'confirmed' | 'reschedule' | 'cancelled' | 'no-answer' | null;

interface CancelReasonChip {
  key: 'client-move' | 'changed-mind' | 'artist-move' | 'studio-closure' | 'price-scope' | 'other';
  label: string;
  reason: RescheduleReason;
  /** Free-text note captured alongside `reason: 'other'`, required by the type when reason is other. */
  note?: string;
}

// Chips map onto the RescheduleReason vocabulary; the three chips without a
// direct RescheduleReason value fold into 'other' with the chip label kept as
// the note, so the specific wording survives even though the reason is coarse.
const CANCEL_REASON_CHIPS: CancelReasonChip[] = [
  { key: 'client-move', label: 'Client requested', reason: 'client-move' },
  { key: 'changed-mind', label: 'Changed their mind', reason: 'other', note: 'Changed their mind' },
  { key: 'artist-move', label: 'Artist unavailable', reason: 'artist-move' },
  { key: 'studio-closure', label: 'Studio closure', reason: 'other', note: 'Studio closure' },
  { key: 'price-scope', label: 'Price / scope', reason: 'other', note: 'Price / scope' },
  { key: 'other', label: 'Other', reason: 'other' },
];

// Reschedule auto-captures a reason the moment a slot is picked (or "no date"
// is chosen) rather than forcing an explicit gate like cancel does - the chip
// is editable via the same CANCEL_REASON_CHIPS list.
const DEFAULT_RESCHEDULE_REASON_CHIP = CANCEL_REASON_CHIPS.find((c) => c.key === 'client-move')!;

// Quick-fill reasons for the late-arrival note (floor mode). Free-text is always
// available too; picking a chip just seeds the note.
const LATE_REASON_CHIPS = ['Traffic', 'Running behind', 'Forgot / no answer', 'On their way'];

const SESSION_LENGTH_LABEL: Record<SessionLength, string> = {
  small: 'Small · ~90 min',
  'half-day': 'Half-day · ~4 hrs',
  'full-day': 'Full-day · ~8 hrs',
  'multi-session': 'Multi-session',
};

const LANE_PIP: Record<ConfirmationLane, string> = {
  'seq-1': 'Sequence 1',
  'seq-2': 'Sequence 2',
  'seq-3': 'Sequence 3',
  'two-day': '2-day text',
  'day-of': 'Day-of',
};

/** Whole-hour starts within a free window, e.g. 10-14 -> [10, 11, 12, 13]. */
function hourSlotsFor(window: { startHour: number; endHour: number }): number[] {
  const out: number[] = [];
  for (let h = Math.ceil(window.startHour); h < window.endHour; h++) out.push(h);
  return out;
}

/**
 * Confirmation work modal (Prompt J, item 2.1). Same three-pane shape as
 * QueueWorkModal / the Sweeper: a booking summary + call-outcome control on
 * the left, the conversation + composer on the right. Renders nothing when
 * the call (or its booking) can't be found; otherwise mounts an inner
 * component keyed by callId so local outcome/reason state resets per call.
 */
export default function ConfirmationWorkModal({ callId, onClose, lateArrival }: Props) {
  const call = useBrainStore((s) => s.confirmationCalls[callId]);
  const booking = useBrainStore((s) => (call ? s.bookings[call.bookingId] : undefined));
  const customer = useBrainStore((s) => (call ? s.customers[call.customerId] : undefined));
  if (!call || !booking || !customer) return null;
  return (
    <ConfirmationWorkModalInner
      key={callId}
      callId={callId}
      booking={booking}
      customer={customer}
      onClose={onClose}
      lateArrival={lateArrival ?? false}
    />
  );
}

function ConfirmationWorkModalInner({
  callId,
  booking: bookingSnapshot,
  customer: customerSnapshot,
  onClose,
  lateArrival,
}: {
  callId: string;
  booking: Booking;
  customer: CustomerRecord;
  onClose: () => void;
  lateArrival: boolean;
}) {
  const navigate = useNavigate();
  const clockIso = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const sessionArtistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const pushNotification = useUIStore((s) => s.pushNotification);

  // Read live from the store so a resolved outcome (confirmedAt, etc.) is
  // reflected immediately; fall back to the snapshot passed in.
  const booking = useBrainStore((s) => s.bookings[bookingSnapshot.id]) ?? bookingSnapshot;
  const liveCustomer = useBrainStore((s) => s.customers[customerSnapshot.id]) ?? customerSnapshot;
  const artists = useBrainStore((s) => s.artists);
  const bookings = useBrainStore((s) => s.bookings);
  const studios = useBrainStore((s) => s.studios);
  const deposits = useBrainStore((s) => s.deposits);
  const waivers = useBrainStore((s) => s.waivers);
  const addRapportNote = useBrainStore((s) => s.addRapportNote);

  const [outcome, setOutcome] = useState<LocalOutcome>(null);
  const [cancelReason, setCancelReason] = useState<CancelReasonChip | null>(null);
  const [followupDraft, setFollowupDraft] = useState<string | undefined>(undefined);
  // Late-arrival mode: the floor manager's note on why the client is late.
  const [lateReasonDraft, setLateReasonDraft] = useState('');
  const [lateReasonLogged, setLateReasonLogged] = useState(false);
  // Late-arrival mode (L1): the mandatory forfeit reason typed before marking a
  // no-show. No chips - the client wanted it to force a considered note.
  const [noShowReasonDraft, setNoShowReasonDraft] = useState('');

  // Reschedule availability picker (Sweeper pick-a-slot pattern): the day/artist
  // currently shown in the slot picker, defaulting to the current artist on the
  // booking's own day so slots are offered immediately with no extra click.
  const [rescheduleDaySelection, setRescheduleDaySelection] = useState<{
    artistId: string;
    dayIso: string;
  }>({ artistId: booking.artistId, dayIso: startOfDay(new Date(booking.start)).toISOString() });
  const [reschedulePicked, setReschedulePicked] = useState<{ artistId: string; newStartIso: string } | null>(
    null,
  );
  const [rescheduleReason, setRescheduleReason] = useState<CancelReasonChip>(DEFAULT_RESCHEDULE_REASON_CHIP);

  const composerSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const artist = artists[booking.artistId];
  const studio = studios[booking.studioId];
  const artistName = artist?.displayName ?? booking.artistId;
  const artistFirstName = artist?.firstName ?? artistName;
  const studioName = studio?.name ?? booking.studioId.replace('-', ' ');

  // Role-gate: artists + apprentices never confirm a booking themselves (vault-06
  // artist-tries path). A null/system persona is treated as Ops, matching how the
  // rest of the modal stamps `by`. When gated off, the escalated booking instead
  // offers Ops the "ask the artist to reach out" action.
  const opsMayConfirm = persona === null || canMarkConfirmed(persona);
  const opsPersonId = selfPersonIdFromSession({
    persona,
    agentId,
    artistId: sessionArtistId,
    apprenticeId,
  });
  const escalated = isConfirmationEscalated(booking);
  const askDeadlineLabel = booking.artistAskDeadlineIso
    ? format(new Date(booking.artistAskDeadlineIso), 'EEE d MMM')
    : null;

  const deposit = booking.depositId ? deposits[booking.depositId] : undefined;
  const depositChip = buildDepositChip(booking, deposit);

  const latestWaiver = Object.values(waivers)
    .filter((w) => w.bookingId === booking.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  const waiverChip = buildWaiverChip(latestWaiver);

  const lane = confirmationWorkDue(booking, clockIso);
  const pip = lane ? LANE_PIP[lane] : null;

  // Late-arrival mode: whole minutes past the scheduled start (floored at 0 for
  // display - the floor only opens this on rows already flagged late).
  const lateBy = lateArrival ? Math.max(0, lateMinutes(booking.start, clockIso)) : 0;

  // Reschedule availability picker: all artists at the booking's studio, no
  // best-fit filter (unlike gap-fill) - the whole studio's week, one week from
  // the booking's own day.
  const rescheduleHeatmap = useMemo(
    () =>
      buildHeatmap({
        artists,
        bookings,
        nowIso: clockIso,
        studioIds: [booking.studioId],
        startIso: startOfDay(new Date(booking.start)).toISOString(),
        days: 7,
      }),
    [artists, bookings, clockIso, booking.studioId, booking.start],
  );
  // Current artist sits first; everyone else keeps buildHeatmap's alphabetical order.
  const rescheduleHeatmapOrdered = useMemo(() => {
    const idx = rescheduleHeatmap.findIndex((r) => r.artist.id === booking.artistId);
    if (idx <= 0) return rescheduleHeatmap;
    return [rescheduleHeatmap[idx]!, ...rescheduleHeatmap.slice(0, idx), ...rescheduleHeatmap.slice(idx + 1)];
  }, [rescheduleHeatmap, booking.artistId]);

  const rescheduleDayArtist = artists[rescheduleDaySelection.artistId];
  const rescheduleDaySchedule = useMemo(
    () =>
      rescheduleDayArtist
        ? buildDaySchedule({ artist: rescheduleDayArtist, bookings, dayIso: rescheduleDaySelection.dayIso, nowIso: clockIso })
        : null,
    [rescheduleDayArtist, bookings, rescheduleDaySelection.dayIso, clockIso],
  );
  // buildDaySchedule only knows the weekly pattern; comp/leave/ad-hoc-RDO days
  // live in leaveBlocks/rdoDates, so the domain day-off guard gates the picker
  // too - an artist off that day must offer no slots.
  const rescheduleDayBookable =
    !!rescheduleDayArtist &&
    !!rescheduleDaySchedule?.working &&
    canScheduleBookingOn(rescheduleDayArtist.availability, new Date(rescheduleDaySelection.dayIso));
  const rescheduleDaySlots = useMemo(
    () => (rescheduleDaySchedule ? rescheduleDaySchedule.freeWindows.flatMap(hourSlotsFor) : []),
    [rescheduleDaySchedule],
  );

  const onCall = () => {
    startOutboundCall({ customerId: liveCustomer.id, studioId: booking.studioId });
  };

  // Floor-only outcomes (late-arrival mode). Check-in is waiver-gated exactly as
  // it is on the floor list, so an unsigned waiver routes the manager into the
  // booking to resolve it rather than silently failing.
  const onCheckInArrived = () => {
    const result = checkInBooking(booking.id);
    if (!result.ok) {
      if (result.reason === 'waiver-unsigned') {
        pushNotification({
          intensity: 'high',
          title: 'Waiver not signed',
          body: `${customerName(liveCustomer)} needs to sign the medical waiver before check-in.`,
          ttlMs: 4000,
        });
        onClose();
        navigate(`/floor/${booking.id}`);
      }
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Checked in',
      body: `${customerName(liveCustomer)} arrived - checked in. Seat them when a chair's ready.`,
      ttlMs: 4000,
    });
    onClose();
  };

  const onMarkNoShow = async () => {
    const reason = noShowReasonDraft.trim();
    if (!reason) return;
    const res = markNoShow(booking.id, { by: currentActorId() ?? 'system', at: clockIso, note: reason });
    if (!res.ok || (res.completion && !(await res.completion))) return;
    pushNotification({
      intensity: 'high',
      title: 'Marked no-show',
      body: `${customerName(liveCustomer)} marked as a no-show - deposit forfeited.`,
      ttlMs: 4000,
    });
    onClose();
  };

  // Log why they were late to the client's record (rapport note), without
  // resolving the booking - the manager can still reschedule / check in after.
  const onLogLateReason = () => {
    const note = lateReasonDraft.trim();
    if (!note) return;
    addRapportNote(liveCustomer.id, `Ran late - ${note}`, 'You', clockIso);
    setLateReasonLogged(true);
  };

  const onFocusComposer = () => {
    composerSectionRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
  };

  const buildFollowupDraft = (): string => {
    if (studio) {
      const [first] = buildConfirmationThread({ booking, customer: liveCustomer, artist, studio });
      if (first) return first.body;
    }
    return `Hi ${liveCustomer.firstName}, tried to reach you about your session on ${simTimeFull(booking.start)}. Reply YES to confirm, or NEW if you need a different time.`;
  };

  const onOutcomeConfirmed = () => {
    resolveConfirmationOutcome(callId, 'confirmed', { by: currentActorId() ?? 'system', at: clockIso });
    onClose();
  };

  // Ops-only escalation action (vault-06 artist-tries path): ask the artist to
  // personally reach out from their own IG, logged in the Ops<->artist DM with a
  // deadline. The modal stays open and re-renders into its "asked" state; Ops -
  // never the artist - later marks the booking confirmed.
  const onAskArtist = () => {
    if (!opsPersonId) return;
    const res = askArtistToConfirm(booking.id, { byPersonId: opsPersonId, at: clockIso });
    if (!res.ok) return;
    pushNotification({
      intensity: 'info',
      title: 'Asked the artist',
      body: `Asked ${artistFirstName} to reach out to ${customerName(liveCustomer)} from their IG.`,
      ttlMs: 4000,
    });
  };

  // ASSUMPTION: the sim call auto-connects (callFlow has no real no-answer signal), so
  // "no answer" is an operator choice that defaults the outcome + reveals the draft.
  // Swap for a real disposition when telephony lands.
  //
  // Single-fire: no-answer resolves but keeps the modal open (for the follow-up
  // draft), so without a guard a second click would re-run recordContactAttempt
  // and over-advance the contact sequence toward its 3-attempt terminal state.
  const onOutcomeNoAnswer = () => {
    if (outcome === 'no-answer') return;
    resolveConfirmationOutcome(callId, 'no-answer', { by: currentActorId() ?? 'system', at: clockIso });
    setOutcome('no-answer');
    setFollowupDraft(buildFollowupDraft());
  };

  const onOutcomeReschedule = () => {
    setOutcome('reschedule');
  };

  const onOutcomeCancelled = () => {
    setOutcome('cancelled');
    setCancelReason(null);
  };

  const onCommitCancel = async () => {
    if (!cancelReason) return;
    const note = cancelReason.note ?? cancelReason.label;
    // The typed reason rides the Ops cancel flow (slot freed, 7-day deposit fork).
    const res = resolveConfirmationOutcome(callId, 'cancelled', {
      by: currentActorId() ?? 'system',
      at: clockIso,
      reason: note,
    });
    if (!res.ok) return;
    if (res.completion && !(await res.completion)) return;
    // Author is the display label 'You' (Persona is a slug union, not a name);
    // persona stays the operator id on the domain `by` stamp above.
    addRapportNote(liveCustomer.id, `Cancelled - ${note}`, 'You', clockIso);
    onClose();
  };

  const onPickRescheduleSlot = (artistId: string, hour: number) => {
    setReschedulePicked({ artistId, newStartIso: hourToIso(rescheduleDaySelection.dayIso, hour) });
  };

  // Picking the slot IS the confirm (Sweeper pattern) - no separate confirm step.
  // rescheduleBooking commits the move first, then the confirmation call outcome
  // is recorded (that branch performs no mutation itself, see confirmationFlow).
  const onCommitReschedule = () => {
    if (!reschedulePicked) return;
    rescheduleBooking({
      bookingId: booking.id,
      reason: rescheduleReason.reason,
      note: rescheduleReason.note,
      pathway: { mode: 'move', artistId: reschedulePicked.artistId, newStartIso: reschedulePicked.newStartIso },
      by: currentActorId() ?? 'system',
      at: clockIso,
    });
    resolveConfirmationOutcome(callId, 'rescheduled', { by: currentActorId() ?? 'system', at: clockIso });
    onClose();
  };

  // "No date" releases the booking (original preserved, no new booking) and keeps
  // the money on file for 12 months (tv-2na.11.13), so it surfaces in the
  // deposit-on-file cohort the Sweeper works.
  // NOTE: buildQueue stays studio-scoped (no per-agent hard filter) to preserve cohort ordering; "back to the agent" is attribution + label, not a separate queue.
  const onReleaseReschedule = () => {
    rescheduleBooking({
      bookingId: booking.id,
      reason: rescheduleReason.reason,
      note: rescheduleReason.note,
      pathway: { mode: 'release' },
      by: currentActorId() ?? 'system',
      at: clockIso,
    });
    resolveConfirmationOutcome(callId, 'rescheduled', { by: currentActorId() ?? 'system', at: clockIso });
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-cream/10 backdrop-blur-sm"
      onClick={onClose}
      data-testid="confirmation-work-modal"
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        className="relative panel bg-ink-deep max-w-[1200px] w-full h-[85vh] rounded-xl flex flex-col overflow-hidden shadow-gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 border-b border-gold-dim/20 px-6 py-4">
          <div className="min-w-0">
            <h2 className="heading-display text-2xl text-cream truncate">{customerName(liveCustomer)}</h2>
            <p className="font-serif text-sm text-cream-soft mt-0.5">
              {artistName} · {studioName} · {depositChip.label.toLowerCase()}
            </p>
            {lateArrival ? (
              <span
                className="inline-flex items-center gap-1.5 mt-2 mr-2 px-2.5 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border border-gold-bright/70 bg-gold/15 text-gold-bright"
                data-testid="cwm-late-banner"
              >
                <AlertTriangle size={11} /> {lateBy} min late · contact them
              </span>
            ) : null}
            {pip ? (
              <span
                className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[10px] font-inter uppercase tracking-widest border border-gold-bright/50 bg-gold/10 text-gold-bright"
                data-testid="cwm-pip"
              >
                {pip}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onCall}
              className="btn-ghost text-xs"
              data-testid="cwm-call"
              title="Start outbound call"
            >
              <Phone size={12} /> Call
            </button>
            <button
              type="button"
              onClick={onFocusComposer}
              className="btn-ghost text-xs"
              data-testid="cwm-text"
              title="Reply by text"
            >
              <MessageSquare size={12} /> Text
            </button>
            <button
              type="button"
              onClick={() => navigate(`/customer/${liveCustomer.id}`)}
              className="btn-quiet text-xs"
              data-testid="cwm-open-record"
            >
              <ExternalLink size={12} /> Open record
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              aria-label="Close"
              data-testid="cwm-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        {/* Two columns */}
        <div className="flex flex-1 min-h-0">
          {/* LEFT: booking summary + call outcome */}
          <aside
            className="w-[360px] shrink-0 border-r border-gold-dim/15 overflow-y-auto px-5 py-5 space-y-5"
            data-testid="cwm-booking"
          >
            <section>
              <p className="label-small-caps text-gold-bright mb-2">Booking</p>
              <div className="panel-dark p-3 space-y-2">
                <BookingLine label="Session" value={simTimeFull(booking.start)} />
                <BookingLine label="Type" value={SESSION_LENGTH_LABEL[booking.sessionLength]} />
                <BookingLine label="Artist" value={artistName} />
                <BookingLine label="Studio" value={studioName} />
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className={clsx(depositChip.cls, 'text-[10px]')}>{depositChip.label}</span>
                  <span className={clsx(waiverChip.cls, 'text-[10px]')}>{waiverChip.label}</span>
                </div>
              </div>
            </section>

            {lateArrival ? (
              <section data-testid="cwm-late-reason">
                <p className="label-small-caps text-gold-bright mb-2">Reason for lateness</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {LATE_REASON_CHIPS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setLateReasonDraft(c);
                        setLateReasonLogged(false);
                      }}
                      className={clsx(
                        'pill-cream text-[11px]',
                        lateReasonDraft === c && 'border-gold-bright/60 bg-gold/10 text-gold-bright',
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <textarea
                  value={lateReasonDraft}
                  onChange={(e) => {
                    setLateReasonDraft(e.target.value);
                    setLateReasonLogged(false);
                  }}
                  rows={2}
                  placeholder="Add a note - logs to the client's record…"
                  className="w-full bg-ink-black/40 border border-gold-dim/30 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright resize-none"
                  data-testid="cwm-late-reason-input"
                />
                <button
                  type="button"
                  onClick={onLogLateReason}
                  disabled={!lateReasonDraft.trim() || lateReasonLogged}
                  className="btn-ghost text-xs mt-2 disabled:opacity-40"
                  data-testid="cwm-late-reason-log"
                >
                  {lateReasonLogged ? 'Reason logged' : 'Log reason'}
                </button>
              </section>
            ) : null}

            <section>
              <p className="label-small-caps text-gold-bright mb-2">
                {lateArrival ? 'Outcome' : 'Call outcome'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {lateArrival ? (
                  <button
                    type="button"
                    onClick={onCheckInArrived}
                    className="btn-ghost text-xs border-mint/60 text-mint hover:bg-mint/10"
                    data-testid="cwm-outcome-checkin"
                  >
                    <Radio size={12} /> They arrived · check in
                  </button>
                ) : opsMayConfirm ? (
                  <button
                    type="button"
                    onClick={onOutcomeConfirmed}
                    className="btn-ghost text-xs border-mint/60 text-mint hover:bg-mint/10"
                    data-testid="cwm-outcome-confirmed"
                  >
                    <Check size={12} /> Confirmed
                  </button>
                ) : (
                  <p
                    className="col-span-2 font-serif text-[12px] text-cream-soft rounded-md border border-gold-dim/30 bg-ink-black/40 px-3 py-2"
                    data-testid="cwm-confirm-blocked"
                  >
                    Artists don&apos;t confirm - ask Ops to confirm once you&apos;ve reached them.
                  </p>
                )}
                {/* Reschedule + cancel are confirmation-call outcomes only. On the
                    floor late-arrival call (L1, 17 Jul) they're redundant - the only
                    late outcomes are check in, no answer, or forfeit-with-reason. */}
                {!lateArrival ? (
                  <button
                    type="button"
                    onClick={onOutcomeReschedule}
                    className="btn-ghost text-xs"
                    data-testid="cwm-outcome-reschedule"
                  >
                    Reschedule
                  </button>
                ) : null}
                {!lateArrival ? (
                  <button
                    type="button"
                    onClick={onOutcomeCancelled}
                    className="btn-ghost text-xs border-blood/60 text-blood hover:bg-blood/10"
                    data-testid="cwm-outcome-cancelled"
                  >
                    Cancel
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onOutcomeNoAnswer}
                  disabled={outcome === 'no-answer'}
                  className="btn-ghost text-xs disabled:opacity-40"
                  data-testid="cwm-outcome-no-answer"
                >
                  No answer
                </button>
              </div>

              {/* Ops-only artist-tries escalation (vault-06). Once the cadence
                  has escalated to Ops, the desk can ask the artist to reach out
                  from their own IG; the artist reports back in the DM and Ops
                  marks it confirmed. Not shown on the floor late-arrival call. */}
              {!lateArrival && escalated && opsMayConfirm ? (
                <div
                  className="mt-3 rounded-md border border-gold-dim/40 bg-gold/5 p-3"
                  data-testid="cwm-artist-tries"
                >
                  <p className="label-small-caps text-gold-bright mb-1">Escalated · artist tries</p>
                  {booking.artistAskedAt ? (
                    <p
                      className="font-serif text-[12px] text-cream-soft"
                      data-testid="cwm-artist-asked"
                    >
                      Asked {artistFirstName}
                      {askDeadlineLabel ? ` - deadline ${askDeadlineLabel}` : ''}. Confirm once they
                      report back.
                    </p>
                  ) : (
                    <>
                      <p className="font-serif text-[12px] text-cream-soft mb-2">
                        {customerName(liveCustomer)} will only answer the artist. Ask{' '}
                        {artistFirstName} to reach out from their own IG, then confirm once they
                        report back.
                      </p>
                      <button
                        type="button"
                        onClick={onAskArtist}
                        className="btn-ghost text-xs w-full justify-center"
                        data-testid="cwm-ask-artist"
                      >
                        Ask {artistFirstName} to reach out
                      </button>
                    </>
                  )}
                </div>
              ) : null}

              {lateArrival ? (
                <div className="mt-3 rounded-md border border-blood/40 bg-blood/5 p-3" data-testid="cwm-noshow-gate">
                  <label
                    htmlFor="cwm-noshow-reason"
                    className="label-small-caps text-blood mb-2 block"
                  >
                    Forfeit reason <span aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="cwm-noshow-reason"
                    value={noShowReasonDraft}
                    onChange={(e) => setNoShowReasonDraft(e.target.value)}
                    rows={2}
                    placeholder="Why is the deposit being forfeited? Required…"
                    className="w-full bg-ink-black/40 border border-gold-dim/30 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
                    data-testid="cwm-noshow-reason"
                  />
                  <button
                    type="button"
                    onClick={onMarkNoShow}
                    disabled={!noShowReasonDraft.trim()}
                    className="btn-ghost text-xs w-full justify-center mt-2 border-blood/60 text-blood hover:bg-blood/10 disabled:opacity-40"
                    data-testid="cwm-outcome-noshow"
                  >
                    <UserX size={12} /> Mark no-show · forfeit deposit
                  </button>
                </div>
              ) : null}

              {outcome === 'reschedule' ? (
                <div className="mt-3 space-y-3" data-testid="cwm-reschedule-pane">
                  {/* Availability picker: all artists at the studio, no filter,
                      current artist first (Sweeper pattern). */}
                  <div className="rounded-md border border-gold-dim/30 bg-ink-black/40 p-3">
                    <p className="label-small-caps text-gold-bright mb-2">
                      All artists · {studioName}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[9px] font-mono">
                        <thead>
                          <tr>
                            <th className="text-left text-cream-faint font-normal pr-1 py-1">
                              Artist
                            </th>
                            {(rescheduleHeatmapOrdered[0]?.cells ?? []).map((cell) => (
                              <th
                                key={cell.dayIso}
                                className="text-cream-faint font-normal px-0.5 py-1 text-center"
                              >
                                {format(new Date(cell.dayIso), 'd/M')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rescheduleHeatmapOrdered.map((row) => (
                            <tr key={row.artist.id}>
                              <td className="pr-1 py-0.5 text-cream-soft truncate max-w-[70px]">
                                {row.artist.displayName}
                              </td>
                              {row.cells.map((cell) => {
                                // Shared cell state so comp/leave/ad-hoc-RDO days
                                // can't drift from the app's other availability grids.
                                const st = availabilityCellState(row.artist.availability, cell);
                                const testId = `cwm-heat-${row.artist.id}-${dayKey(cell.dayIso)}`; // studio day (tv-2na.15.17)
                                if (st.kind === 'comp' || st.kind === 'leave') {
                                  const m = DAY_OFF_MARKER[st.kind];
                                  return (
                                    <td key={cell.dayIso} className="px-0.5 py-0.5">
                                      <div
                                        data-testid={testId}
                                        title={`${row.artist.displayName} · ${m.label}${st.note ? ` - ${st.note}` : ''}`}
                                        className={clsx(
                                          'w-full h-5 rounded-sm flex items-center justify-center border border-transparent',
                                          m.cls,
                                        )}
                                      >
                                        {m.letter}
                                      </div>
                                    </td>
                                  );
                                }
                                const isSelected =
                                  rescheduleDaySelection.artistId === row.artist.id &&
                                  rescheduleDaySelection.dayIso === cell.dayIso;
                                return (
                                  <td key={cell.dayIso} className="px-0.5 py-0.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setRescheduleDaySelection({
                                          artistId: row.artist.id,
                                          dayIso: cell.dayIso,
                                        });
                                        setReschedulePicked(null);
                                      }}
                                      disabled={st.off}
                                      title={
                                        st.off
                                          ? `${row.artist.displayName} off`
                                          : `${row.artist.displayName} · ${st.freeHours}/${st.workingHours}h free`
                                      }
                                      data-testid={testId}
                                      className={clsx(
                                        'w-full h-5 rounded-sm border transition',
                                        st.off
                                          ? 'bg-ink-black/60 text-cream-faint/30 cursor-not-allowed opacity-50'
                                          : intensityClass(st.freeHours, st.workingHours),
                                        isSelected ? 'border-gold-bright' : 'border-transparent',
                                      )}
                                    >
                                      {st.off ? '·' : st.freeHours}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="font-serif text-[11px] text-cream-faint mt-2">
                      {artistName}, the current artist, sits first - no best-fit filter, the whole
                      studio&apos;s week.
                    </p>
                  </div>

                  {/* Picked day's free windows, offered as pickable hourly slots.
                      Gated by the domain day-off guard (comp/leave/ad-hoc RDO),
                      not just the weekly pattern. */}
                  {rescheduleDayBookable && rescheduleDaySchedule ? (
                    <div className="rounded-md border border-gold-dim/30 bg-ink-black/40 p-3">
                      <p className="label-small-caps text-gold-bright mb-2">
                        {rescheduleDayArtist?.displayName ?? rescheduleDaySelection.artistId} ·{' '}
                        {format(new Date(rescheduleDaySelection.dayIso), 'EEE d MMM')}
                      </p>
                      {rescheduleDaySlots.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {rescheduleDaySlots.map((h) => {
                            const iso = hourToIso(rescheduleDaySelection.dayIso, h);
                            const isPicked =
                              reschedulePicked?.artistId === rescheduleDaySelection.artistId &&
                              reschedulePicked?.newStartIso === iso;
                            return (
                              <button
                                key={`${rescheduleDaySelection.artistId}-${h}`}
                                type="button"
                                onClick={() => onPickRescheduleSlot(rescheduleDaySelection.artistId, h)}
                                data-testid={`cwm-slot-${rescheduleDaySelection.artistId}-${h}`}
                                className={clsx(
                                  'pill-cream text-[11px]',
                                  isPicked && 'border-gold-bright/60 bg-gold/10 text-gold-bright',
                                )}
                              >
                                {simTimeShort(iso)}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="font-serif text-[12px] text-cream-soft">
                          Fully booked this day.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-serif text-[12px] text-cream-soft">
                      {rescheduleDayArtist?.displayName ?? rescheduleDaySelection.artistId} is
                      rostered off this day.
                    </p>
                  )}

                  {/* Captured outcome, once a slot is picked - the reason is
                      auto-captured (default "Client requested") and editable via
                      the same reason chips cancel uses. */}
                  {reschedulePicked ? (
                    <div
                      className="rounded-md border border-mint/40 bg-mint/5 p-3"
                      data-testid="cwm-reschedule-captured"
                    >
                      <span className="pill-mint text-[10px]">Captured</span>
                      <p className="font-serif text-[13px] text-cream mt-2">
                        Rescheduled -&gt;{' '}
                        {artists[reschedulePicked.artistId]?.displayName ?? reschedulePicked.artistId}{' '}
                        {simTimeFull(reschedulePicked.newStartIso)}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {CANCEL_REASON_CHIPS.map((chip) => (
                          <button
                            key={chip.key}
                            type="button"
                            onClick={() => setRescheduleReason(chip)}
                            className={clsx(
                              'pill-cream text-[11px]',
                              rescheduleReason.key === chip.key &&
                                'border-mint/60 bg-mint/10 text-mint',
                            )}
                            data-testid={`cwm-reason-${chip.key}`}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                      <p className="font-serif text-[11px] text-cream-faint mt-2">
                        Reason auto-captured, edit if it wasn&apos;t the client&apos;s call.
                      </p>
                      <button
                        type="button"
                        onClick={onCommitReschedule}
                        className="btn-primary text-xs mt-3"
                        data-testid="cwm-reschedule-commit"
                      >
                        Move booking &amp; close
                      </button>
                    </div>
                  ) : null}

                  {/* No date yet (board 06, tv-2na.11.13): release the slot (the booking
                      is kept, not deleted); the deposit stays on file for 12 months and
                      the Sweeper chases it. */}
                  <div className="rounded-md border border-dashed border-gold-dim/40 p-3">
                    <p className="font-serif text-[12px] text-cream-soft mb-2">
                      Can&apos;t settle a date on the call? No date releases the slot and keeps
                      the deposit on file for 12 months; the Sweeper chases it.
                    </p>
                    <button
                      type="button"
                      onClick={onReleaseReschedule}
                      className="btn-ghost text-xs w-full justify-center"
                      data-testid="cwm-reschedule-nodate"
                    >
                      No date · to the Sweeper (deposit on file 12 months)
                    </button>
                  </div>
                </div>
              ) : null}

              {outcome === 'cancelled' ? (
                <div
                  className="mt-3 rounded-md border border-blood/40 bg-blood/5 p-3"
                  data-testid="cwm-cancel-gate"
                >
                  <p className="label-small-caps text-blood mb-2">Reason required</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CANCEL_REASON_CHIPS.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={() => setCancelReason(chip)}
                        className={clsx(
                          'pill-cream text-[11px]',
                          cancelReason?.key === chip.key && 'border-blood/60 bg-blood/10 text-blood',
                        )}
                        data-testid={`cwm-reason-${chip.key}`}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    disabled={!cancelReason}
                    onClick={onCommitCancel}
                    className="btn-primary text-xs mt-3"
                    data-testid="cwm-cancel-commit"
                  >
                    Confirm cancel &amp; close
                  </button>
                </div>
              ) : null}
            </section>
          </aside>

          {/* RIGHT: conversation + composer */}
          <section ref={composerSectionRef} className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <ConversationThread customer={liveCustomer} />
            </div>
            {outcome === 'no-answer' && followupDraft ? (
              <p
                className="px-6 pt-2 font-inter text-[10px] uppercase tracking-widest text-gold-bright"
                data-testid="cwm-followup-draft"
              >
                Follow-up draft · auto-filled since there was no answer
              </p>
            ) : null}
            <CustomerComposer
              customer={liveCustomer}
              host="confirmations"
              initialChannel="sms"
              initialDraft={outcome === 'no-answer' ? followupDraft : undefined}
            />
          </section>
        </div>
      </motion.div>
    </motion.div>
  );
}

function BookingLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">{label}</span>
      <span className="font-serif text-sm text-cream">{value}</span>
    </div>
  );
}
